/**
 * Every MEDIS (Azure) endpoint wraps its payload in a common envelope:
 *
 *   { State: "success", StateNum: 0, ErrorMessage: null, ErrFlag: false, Response: <T> }
 *
 * These helpers unwrap it and turn business-level failures into a typed error,
 * so feature code never has to inspect `State` by hand.
 */

export interface MedisEnvelope<T> {
  State: string;
  StateNum?: number;
  ErrorMessage?: string | null;
  ErrFlag?: boolean;
  DebugString?: string | null;
  Response: T;
}

export class MedisError extends Error {
  constructor(
    message: string,
    readonly state: string,
    readonly stateNum?: number,
  ) {
    super(message);
    this.name = 'MedisError';
  }
}

export function isMedisSuccess<T>(envelope: MedisEnvelope<T>): boolean {
  return typeof envelope?.State === 'string' && envelope.State.toLowerCase() === 'success';
}

/** Returns the inner `Response`, or throws `MedisError` if the envelope reports failure. */
export function unwrapMedis<T>(envelope: MedisEnvelope<T>): T {
  if (!isMedisSuccess(envelope)) {
    throw new MedisError(
      envelope?.ErrorMessage || 'Cererea către serviciul medical a eșuat.',
      envelope?.State ?? 'unknown',
      envelope?.StateNum,
    );
  }
  return envelope.Response;
}

/** Heuristic for "MEDIS session expired" — used to trigger an access-code refresh + retry. */
export function isMedisSessionInvalid(status: number | undefined, body: unknown): boolean {
  if (status === 401 || status === 407) return true;
  const message =
    body && typeof body === 'object' && 'ErrorMessage' in body
      ? String((body as { ErrorMessage?: unknown }).ErrorMessage ?? '')
      : '';
  return /invalid session|session.*(expired|invalid)/i.test(message);
}
